// src/components/frontpage/DirectorySection.jsx
//
// Shared, section-aware primitives for the Locality Directory surface.
//
// The three Directory sections (Directory categories · Official Contacts · Utility
// Services) each used to carry their own copy of a near-identical section title, row,
// and accordion-branch markup — three definitions of the same chrome, two of them
// hard-coding the locality blue instead of reading the theme (see
// docs/research/DIRECTORY_STATUS_REPORT.md §1). These primitives are the single source of truth
// for that chrome: one font constant, one theme-routed colour, one row, one branch rule.
//
// Behaviour — the single-open invariant and both drill styles ('accordion' | 'popout') —
// stays owned by the section components (DirectoryNav / ContactList / UtilityCategoryList)
// and DirectoryColumn. These primitives are presentation only.

import React from 'react';
import { useTheme } from '../../themes/ThemeProvider';
import { useTranslation } from '../../i18n/useTranslation';
import { tokens } from '../../styles/designTokens';
import { BORDER_WIDTH } from '../events/EventsMiniCard';

// The one font declaration for the whole Directory surface (OpenSauceSans only — the
// project font rule). Import this instead of repeating the literal.
export const DIR_FONT = 'OpenSauceSans, sans-serif';

// The accordion branch's left-rule colour (AccordionBranch below). PresencePanel used to
// carry a matching hairline of its own; that was removed (hairline-and-indent correction,
// 16 Aug) once every PresencePanel mount was also wrapped in the open-entry border — a
// second framing device on the same content was redundant, and the indent cost width a
// narrow column can't spare. This token now has one consumer, not two.
export const ACCORDION_RULE_COLOR = tokens.colors.borderSecondary;

// Leading context-colour dot for an INDIVIDUAL entry/leaf row (an actual listing), NOT a
// category header. One definition, theme-routed (blue in locality) — used by DirectoryRow's
// `bullet` prop (Official Contacts) and by the bespoke Directory-entry / Utility-provider
// name rows, so all three sections' listings share the same marker. `marginTop` centres the
// 6px dot against the first 13px/1.35 text line (rows are aligned flex-start so long names
// wrap under, not around, the dot).
export function EntryBullet() {
  const theme = useTheme();
  return (
    <span aria-hidden="true" style={{
      flexShrink: 0,
      width: '6px',
      height: '6px',
      marginTop: '6px',
      borderRadius: '50%',
      background: theme.primary
    }} />
  );
}

// Section heading (uppercase, theme blue). Replaces the three duplicated title styles.
// Colour comes from the theme (locality = blue), never a hard-coded hex.
//
// onClick (Directory column shell brief, Scope 3): when provided, the heading becomes the
// section's exclusive-concertina toggle — renders as a full-width, left-aligned <button> with
// the chrome reset to zero so it looks identical to the plain heading it replaces. Follows the
// same raw-button precedent as this file's own DirectoryRow below (no written StandardButton
// carve-out for either — an existing, unexamined local pattern, not a new one). Omit onClick for
// the original non-interactive heading (unchanged).
export function SectionTitle({ title, marginBottom = '4px', onClick = null }) {
  const theme = useTheme();
  if (!title) return null;
  const baseStyle = {
    fontFamily: DIR_FONT,
    fontWeight: '700',
    fontSize: '14px',
    color: theme.primary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom
  };
  if (!onClick) {
    return <div style={baseStyle}>{title}</div>;
  }
  return (
    <button
      onClick={onClick}
      style={{
        ...baseStyle,
        display: 'block',
        width: '100%',
        textAlign: 'left',
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer'
      }}
    >
      {title}
    </button>
  );
}

// The open-ENTRY outline (border discipline correction, 16 Aug) — Events' "mark the open,
// focused card" treatment (src/components/events/EventsMiniCard.jsx's BORDER_WIDTH, imported
// not duplicated, so tuning one retunes both) reused verbatim, in the column's own context
// colour. Marks ONLY the open entry's detail panel (a listing/contact/provider) — never a
// section, never a category. One definition so DirectoryNav/ContactCard/UtilityCategoryList
// apply an identical border rather than separate copies that could drift. Border only —
// callers keep their own radius/padding/background, which already differ by drill style
// (accordion inline box vs. popout floating card).
export function openEntryStyle(theme) {
  return { border: `${BORDER_WIDTH}px solid ${theme.primary}` };
}

// Encloses an entry's row and its (conditionally rendered) detail content in one bordered
// unit when open — openEntryStyle must frame the name and its detail together, not sit only
// around the detail beneath a name that reads as unbounded chrome above it (border-encloses-
// name correction, 16 Aug). One definition so DirectoryNav / UtilityCategoryList /
// ContactList+ContactCard's accordion (in-flow) paths apply identical framing instead of
// three separate wrap-or-don't-wrap implementations. Popout paths (DirectoryNav's
// DetailPopout, ContactCard's own popout branch) already show the name inside their own
// floating card and are untouched — this only covers the in-flow accordion case, where the
// row and its detail are visually separate siblings today.
const OPEN_ENTRY_FRAME_PADDING = 8;

// The total horizontal space the frame's own padding + border take up on its left side when
// open, exported so a caller whose own container has no padding to spare can reserve exactly
// this much room itself (border-position correction, 16 Aug — see OpenEntryFrame below and
// ContactList.jsx's use of it).
export const OPEN_ENTRY_FRAME_INSET = OPEN_ENTRY_FRAME_PADDING + BORDER_WIDTH;

export function OpenEntryFrame({ isOpen, children }) {
  const theme = useTheme();
  return (
    <div style={isOpen ? {
      padding: `${OPEN_ENTRY_FRAME_PADDING}px`,
      // Without this, opening an entry pushes its name right by OPEN_ENTRY_FRAME_INSET (the
      // padding above plus the border's own width both eat into the left side) — breaking
      // vertical alignment with closed neighbour rows, since they carry neither (browser
      // verification, 16 Aug: "Marianna Seamstress" no longer lined up with "Manuela Duarte" /
      // "Sofia SMdP Laundry" once opened). The negative margin exactly cancels that inset —
      // in CSS box-model order (margin, border, padding, content), content sits at
      // marginLeft + border + padding from the frame's own position, so setting
      // marginLeft = -(padding + border) always nets to 0, regardless of padding's actual
      // value or of whatever padding the frame's own container happens to have. Content
      // doesn't move; only the frame's own left edge — where the border is drawn — moves
      // left to make room, which is the visible effect Michael asked for ("the border should
      // sit to the left of the name column"). Relies on the caller having OPEN_ENTRY_FRAME_INSET
      // worth of spare room to its left before an overflow:hidden ancestor — true today at
      // DirectoryNav's and UtilityCategoryList's accordion sites (nested inside
      // AccordionBranch's own 28px+14px indent), NOT true at ContactList's site, which
      // reserves it explicitly — see ContactList.jsx.
      marginLeft: `${-OPEN_ENTRY_FRAME_INSET}px`,
      borderRadius: '8px',
      ...openEntryStyle(theme)
    } : undefined}>
      {children}
    </div>
  );
}

// The small area label after a listing's name (Roadmap #87, R2): a wider listing names its
// area ("Alcobaça"), a national one says "National"; the area's own listings carry none. One
// definition for all three sections and both drill styles. Takes a row carrying areaName /
// distance / isNational (useDirectoryListings' areaFields). Area names are data, not translated.
export function areaLabelText(row, t) {
  if (!row) return null;
  if (row.isNational) return t('dir_area_national');
  if (row.distance != null && row.distance > 0 && row.areaName) return row.areaName;
  return null;
}

export function AreaLabel({ row }) {
  const { t } = useTranslation('frontpage');
  const text = areaLabelText(row, t);
  if (!text) return null;
  return (
    <span style={{ fontSize: '10px', fontWeight: '400', color: tokens.colors.textSecondary, marginLeft: '6px', whiteSpace: 'nowrap' }}>
      {text}
    </span>
  );
}

// One directory row: icon + label + optional count, with the shared open/hover states.
// Serves both category rows (Directory, Utilities — pass a count) and leaf rows
// (Official Contacts — no count). `isLinked` bolds the label (verified-group link).
export function DirectoryRow({
  icon = null,
  label,
  count = null,
  isOpen = false,
  isLinked = false,
  onClick,
  compact = false,
  bullet = false,  // leading context-colour dot — individual entry/leaf rows only, never category headers
  areaRow = null   // a listing row: shows its AreaLabel after the name (Roadmap #87, R2)
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px',
        width: '100%',
        minHeight: compact ? '22px' : '26px',
        background: isOpen ? '#e8edf3' : 'none',
        border: 'none',
        cursor: 'pointer',
        fontFamily: DIR_FONT,
        fontSize: '13px',
        lineHeight: '1.35',
        color: '#1f2937',
        padding: '2px 4px',
        borderRadius: '4px',
        transition: 'background 0.15s',
        textAlign: 'left'
      }}
      onMouseEnter={(e) => { if (!isOpen) e.currentTarget.style.background = '#f0f3f7'; }}
      onMouseLeave={(e) => { if (!isOpen) e.currentTarget.style.background = 'none'; }}
    >
      {bullet && <EntryBullet />}
      {icon != null && (
        <span style={{ fontSize: '14px', flexShrink: 0, width: '20px', textAlign: 'center' }}>
          {icon}
        </span>
      )}
      <span style={{ flex: 1, minWidth: 0, fontWeight: isLinked ? '700' : '400' }}>
        {label}
        {areaRow && <AreaLabel row={areaRow} />}
      </span>
      {count != null && count > 0 && (
        <span style={{ fontSize: '10px', color: '#6b7280', flexShrink: 0, marginLeft: 'auto' }}>
          {count}
        </span>
      )}
    </button>
  );
}

// The indented accordion branch holding a category's expanded children (Directory
// entries, Utility providers). One left-rule colour + indent, shared by DirectoryNav and
// UtilityCategoryList's category-level markup — unrelated to entry-level framing (see
// ACCORDION_RULE_COLOR above; PresencePanel no longer has a rule of its own to centralise
// against).
export function AccordionBranch({ children }) {
  return (
    <div style={{
      padding: '4px 0 0 28px',
      borderLeft: `2px solid ${ACCORDION_RULE_COLOR}`,
      marginLeft: '14px',
      marginBottom: '0'
    }}>
      {children}
    </div>
  );
}
